import { Fragment, useMemo, useState } from 'react'

import { Box, Button, Flex } from 'app/components/design-system-components'
import Badge from 'app/components/design-system-components/Badges'
import { UndoRounded } from 'app/components/design-system-components/icons/content'
import HeadingAndLabel, {
  STATUS_CONFIRMED,
} from 'app/components/display/smartdocs/headingAndLabel' // STATUS_UNVERIFIED,
import {
  getTotalAndCheckItemNumber,
  normalizeSmartDocsAnalysisBlocks,
  sortSmartDocsFieldsForPM,
} from 'app/components/display/smartdocs/helpers'
import theme from 'app/match/applicationReportPDF/assets/theme'
import Spinner from 'app/sm/common/spinner'
import {
  SMART_DOCS_DOC_STATE_ANALYZING,
  SMART_DOCS_DOC_STATE_CONVERTING,
} from 'app/utils/applications/proofofincome/api'

const SHOW_MORE = 'Show More'
const SHOW_LESS = 'Show Less'

export interface DataType {
  analysis: Analysis
  /** Per-document state from list analysis API (`converting` | `analyzing` | `ready`). */
  document_states?: Record<string, string>
  /** Doc-type → job id when analysis jobs are in flight (API `processing_jobs`). */
  processing_jobs?: Record<string, string>
}

export interface ConfirmationStatus {
  confirmed_at: string
  confirmed_by: string
  id: string
}

/** Mirrors API `DocumentFieldAnalysisResponse` (`pkg/sm/smartdocs/usecase/response.go`). */
export interface AnalysisItem {
  id: string
  label: string
  status: string
  message: string
  query?: string
  field_key?: string
  importance?: string
  confirmation_details: ConfirmationStatus[] | null
  /** Added client-side when merging list analysis with attachment metadata (not from list API). */
  doc_id?: string
  file_name?: string
}

export interface AnalysisDocumentBlock {
  fields: AnalysisItem[] | null
  is_valid: boolean
}

export interface Analysis {
  [key: string]: AnalysisDocumentBlock | AnalysisDocumentBlock[]
}

/** API tracks refresh at the document level via `document_states[docId]`. */
const hasDocumentLevelSmartDocsRefresh = (payload?: DataType) => {
  const states = payload?.document_states
  if (!payload?.analysis || !states) {
    return false
  }

  return Object.keys(payload.analysis).some((docId) => {
    const state = states[docId]
    return (
      state === SMART_DOCS_DOC_STATE_CONVERTING ||
      state === SMART_DOCS_DOC_STATE_ANALYZING
    )
  })
}

const compactAnalysisItems = (
  fields: Array<AnalysisItem | undefined>,
): AnalysisItem[] =>
  fields.filter((field): field is AnalysisItem => Boolean(field))

interface ExplainerProps {
  data?: DataType | undefined
  isCopilot?: boolean
  onClickConfirmOKForAnalysis?: (analysisGUID: string) => void
  onClickUndoForAnalysis?: (
    analysisGUID: string,
    confirmationGUID: string,
  ) => void
  isRenterSide?: boolean
  showOpenViewerLink?: boolean
  toggleDocumentsViewer?: ((docGUID: string) => void) | undefined
}

function Explainer({
  data,
  isCopilot = false,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
  isRenterSide = true,
  showOpenViewerLink = false,
  toggleDocumentsViewer,
}: ExplainerProps) {
  const [showMoreEnabled, setShowMoreEnabled] = useState(false)
  const displayData = useMemo(() => {
    if (!data) {
      return []
    }

    return Object.entries(data.analysis).flatMap(
      ([analysisKey, docAnalysis]) => {
        const blocks =
          normalizeSmartDocsAnalysisBlocks<AnalysisItem, AnalysisDocumentBlock>(
            docAnalysis,
          )

        return blocks
          .map((block, blockIdx) => {
            const fields = block.fields as AnalysisItem[]

            return {
              ...block,
              fields: isRenterSide ? fields : sortSmartDocsFieldsForPM(fields),
              analysisListKey:
                blocks.length > 1 ? `${analysisKey}:${blockIdx}` : analysisKey,
            }
          })
          .filter((block) => block.fields.length > 0)
      },
    )
  }, [data, isRenterSide])
  const hasSmartDocsRefreshInProgress = useMemo(
    () => hasDocumentLevelSmartDocsRefresh(data),
    [data],
  )
  const { totalDocNumber, docNumberToCheck } =
    getTotalAndCheckItemNumber(displayData)
  const showLink =
    !hasSmartDocsRefreshInProgress &&
    (totalDocNumber !== docNumberToCheck ||
      (!isRenterSide && displayData.some((block) => block.fields.length > 1)))

  const [showMoreText, setShowMoreText] = useState(SHOW_MORE)
  const changeDisplayData = () => {
    if (showMoreEnabled) {
      setShowMoreText(SHOW_MORE)
    } else {
      setShowMoreText(SHOW_LESS)
    }
    setShowMoreEnabled(!showMoreEnabled)
  }

  if (displayData.length === 0) {
    return null
  }

  const onClickConfirmOK = (analysisGUID: string) => {
    if (onClickConfirmOKForAnalysis) {
      onClickConfirmOKForAnalysis(analysisGUID)
    }
  }

  const onClickUndo = (analysisGUID: string, confirmationGUID: string) => {
    if (onClickUndoForAnalysis) {
      onClickUndoForAnalysis(analysisGUID, confirmationGUID)
    }
  }

  return (
    <Box width="100%" lineHeight="1.4">
      {hasSmartDocsRefreshInProgress ? (
        <Flex flexDirection="column" alignItems="stretch" py={2}>
          <Flex alignItems="center" mb={3}>
            <Badge text="Processing" variant="warning" />
          </Flex>
          <Box
            fontSize={theme.fontSizes.pRegular14}
            color={theme.colors.gray600}
            mb={2}
          >
            SmartDocs is refreshing your document analysis. This may take a
            moment.
          </Box>
          <Flex width="100%" justifyContent="center" mt={2}>
            <Spinner text={undefined} />
          </Flex>
        </Flex>
      ) : (
        <Fragment>
          {displayData?.map(({ fields, analysisListKey }) => {
            const firstPmCollapsedField = fields.find(
              (field) => field.status !== 'verified',
            )
            const visibleFields =
              !isRenterSide && !showMoreEnabled
                ? compactAnalysisItems([firstPmCollapsedField])
                : fields

            return (
              <Box key={`smartdoc-${analysisListKey}`}>
                {visibleFields.map((itemData, fieldIdx) => {
                  let {
                    label,
                    status,
                    message,
                    confirmation_details,
                    id,
                    doc_id,
                    file_name,
                  } = itemData
                  const validConfirmationStatusRenter =
                    confirmation_details?.filter(
                      (item) => item.confirmed_by === 'renter_confirmed',
                    )[0]
                  const validConfirmationStatusPM =
                    confirmation_details?.filter(
                      (item) => item.confirmed_by === 'pm_confirmed',
                    )[0]
                  if (
                    (isRenterSide && validConfirmationStatusRenter) ||
                    (!isRenterSide && validConfirmationStatusPM)
                  ) {
                    status = STATUS_CONFIRMED
                  }
                  if (!showMoreEnabled) {
                    if (itemData.status === 'verified') {
                      return null
                    }
                  }
                  const safeDocId = doc_id
                  const safeFileName = file_name ?? ''
                  return (
                    <Box
                      key={id || `${label}-${fieldIdx}`}
                      mb={5}
                      lineHeight="1.4"
                    >
                      <HeadingAndLabel
                        heading={label}
                        status={status}
                        showOpenViewerLink={showOpenViewerLink}
                        toggleDocumentsViewer={
                          toggleDocumentsViewer && safeDocId
                            ? () => toggleDocumentsViewer(safeDocId)
                            : () => {}
                        }
                        fileName={safeFileName}
                      />
                      <Box fontSize={theme.fontSizes.pRegular14} mb={3} mt={1}>
                        {message}
                      </Box>
                      {isRenterSide
                        ? !validConfirmationStatusRenter &&
                          !isCopilot && (
                            <Button
                              variant="outlineGray"
                              sizeVariant="extraSmall"
                              onClick={() => onClickConfirmOK(id)}
                            >
                              <Flex alignItems="center">
                                <Box>Confirm Ok</Box>
                              </Flex>
                            </Button>
                          )
                        : !validConfirmationStatusPM &&
                          !isCopilot && (
                            <Button
                              variant="outlineGray"
                              sizeVariant="extraSmall"
                              onClick={() => onClickConfirmOK(id)}
                            >
                              <Box>Confirm Ok</Box>
                            </Button>
                          )}
                      {isRenterSide
                        ? validConfirmationStatusRenter &&
                          !isCopilot && (
                            <Button
                              variant="outlineGray"
                              sizeVariant="extraSmall"
                              onClick={() =>
                                onClickUndo(
                                  id,
                                  validConfirmationStatusRenter?.id,
                                )
                              }
                            >
                              <Flex alignItems="center">
                                <UndoRounded /> <Box ml={3}>Undo</Box>
                              </Flex>
                            </Button>
                          )
                        : validConfirmationStatusPM &&
                          !isCopilot && (
                            <Button
                              variant="outlineGray"
                              sizeVariant="extraSmall"
                              onClick={() =>
                                onClickUndo(id, validConfirmationStatusPM?.id)
                              }
                            >
                              <Flex alignItems="center">
                                <UndoRounded /> <Box ml={3}>Undo</Box>
                              </Flex>
                            </Button>
                          )}
                    </Box>
                  )
                })}
              </Box>
            )
          })}
          {showLink && (
            <Button variant="linkBlueWithoutWeight" onClick={changeDisplayData}>
              {showMoreText}
            </Button>
          )}
        </Fragment>
      )}
    </Box>
  )
}

export default Explainer
